import { Inject, Injectable } from "@angular/core";
import isEmpty from "lodash-es/isEmpty";
import {fromPairs} from "lodash-es";
import { LOCAL_STORAGE } from "../../../storage/src/lib/tokens/local-storage";
import { getCookie } from "./browserAbstraction";
import { WINDOW } from "@nx-averycom/shared-storage";

@Injectable({
  providedIn: 'root'
})
export class BrowserAbstractionService {


  constructor(@Inject(LOCAL_STORAGE) readonly localStorage: Storage, @Inject(WINDOW) readonly window: Window) {
  }

  /**
   * Function validates if string is a JSON object.
   * @param  {string} value - Test string
   * @return {boolean}
   */
  isStringJSON(value: string) {
    try {
      JSON.parse(value);
    } catch (e) {
      return false;
    }
    return true;
  }

  getLocalStorage(key: string) {
    let value;

    try {
      value = this.localStorage.getItem(key) || '';
    } catch (err) {
      console.error(`local storage is not supported; ${err}`);
      return false;
    }

    if (this.isStringJSON(value) && typeof value === 'string') {
      return JSON.parse(value);
    }

    return value;
  }

  /**
   * Function that handles local storage & cookie data retrieval.
   * Local storage data takes priority over cookie data.
   * @param key
   * @return {any}
   */
  localStorageGetter(key: string) {
    return this.getLocalStorage(key) || this.getCookie(key) || null;
  }

  /**
   * Function that handles local storage & cookie data creation.
   * Local storage data takes priority over cookie data.
   * @param key
   * @param value
   */
  localStorageSetter(key: string, value: any) {
    this.setLocalStorage(key, value) || this.createCookie(key, value, 99999);
  }

  /**
   * Function used to delete key/value pair from local storage
   * @param key
   * @return {boolean}
   */
  localStorageRemoveItem(key: string) {
    try {
      this.localStorage.removeItem(key);
      return true;
    } catch (err) {
      console.error(`local storage is not supported; ${err}`);
      return false;
    }
  }

  setLocalStorage(key: string, value: any) {
    let valueStringified = value;

    if (!isEmpty(value)) {
      if (typeof value === 'object') {
        valueStringified = JSON.stringify(value);
      }
    }

    try {
      this.localStorage.setItem(key, valueStringified);
      return true;
    } catch (err) {
      console.error(`local storage is not supported; ${err}`);
      return false;
    }
  }
  getCookie(key: string) {
    try {
      const match = document.cookie.match(new RegExp(key + '=([^;]+)'));
      const val = match && match[1].replace(/'/g, '');

      if (val === null) return null;
      return (this.isStringJSON(val) && JSON.parse(val)) || val;
    } catch (err) {
      console.error('Cookies have been disabled.');
      return null;
    }
  }
  /**
   * Function used to create a cookie.
   * Specify a number for @days for a persistent cookie.
   * The value can either be a string or an object.
   * @param key
   * @param value
   * @param days
   * @param domain
   */
  createCookie(key: string, value: any, days: number, domain: string = '') {
    try {
      let expires, finalDomain;

      if (days) {
        const date = new Date();
        date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
        expires = `; expires=${date.toUTCString()}`;
      } else {
        expires = '';
      }
      if (domain !== '') {
        finalDomain = `;domain=${domain}`;
      } else {
        finalDomain = '';
      }

      const setValue: string = value instanceof Object ? `'${JSON.stringify(value)}'` : value;
      document.cookie = `${key}=${setValue}${expires}${finalDomain};path=/`;
    } catch (err) {
      console.error('Cookies have been disabled.');
    }
  }

  /**
   * Destructure the toggle query parameter
   *
   * @export
   * @param {string} toggleString
   * @returns {string[]}
   */
  destructureToggleParam(toggleString: string): string[] {
    return toggleString.split(',');
  }

  /**
   * Destructures the filter query parameters so that the application can utilize the data
   *
   * @export
   * @param {string} filterString
   * @returns
   */
  destructureFilterParam(filterString: string) {
    const types = filterString.split('~');
    return fromPairs(
      types.map((type) => {
        const [key, values] = type.split('_');

        return [`${key}`, values.split(',')];
      })
    );
  }

  /**
   * Since we need to make elastic search calls, we need to undo the readable url and convert the
   * characters back to its original characters. It is important to also know that order matters here,
   * just like toBrowser().
   *
   * @export
   * @param {string} [snippet='']
   * @returns {string}
   */
  fromBrowser(snippet: string = ''): string {
    return snippet
      .replace(/__/g, '&#44;')
      .replace(/--/g, '/')
      .replace(/-/g, ' ')
      .replace(/~/g, '&')
      .replace(/_/g, '-');
  }

  checkAppThemeMode(): Promise<string> {
    // TODO: rework the entire existing theme mode system later!
    // these paths do not have industrial url paths, therefore,
    // the only way to persist theme mode is to check for theme mode cookie
    // of the previous path that navigates to them
    const specialPaths = [
      'cart.avery.com', // cart domain
      '/myaccount/', // myaccount paths
      '/checkout/', // cart checkout flow
      '/secure/', // secure paths
      '/srch', // search page
      '/custom-printing/labels/rolls', // browse/create flow that dpo redirects to
    ];
    let themeMode = 'default';

    // if current path is a special path, use cookie instead of rely on url
    for (const path of specialPaths) {
      if (this.window.location.href.includes(path)) {
        themeMode = getCookie('avythmmde') ? 'industrial' : 'default';
        return Promise.resolve(themeMode);
      }
    }

    // if current path has /industrial/ in url string, set theme mode accordingly
    themeMode = this.window.location.pathname.includes('/industrial/')
      ? 'industrial'
      : 'default';

    // remove cookie for default theme, add cookie for industrial theme
    if (themeMode === 'default') {
      // createCookie(config.theme.cookieKey, '', -1, '.avery.com');
    } else {
      // createCookie(config.theme.cookieKey, themeMode, 7, '.avery.com');
    }

    return Promise.resolve(themeMode);
  }
}
